import React from 'react';
import { throttle } from 'lodash';
import moment from 'moment-timezone';
import { Props as AsyncSelectItemProps } from 'components/AsyncSelectItem';
import CoachingConstants, {
  CoachingSessionFrequency,
  CoachingSessionDuration,
} from 'coaching/CoachingConstants';
import Coin from 'components/Coin';
import Columns2 from 'components/Columns2';
import CoachingCoordinatorAvailabilityConflicts from './CoachingCoordinatorAvailabilityConflicts';
import CoachingCoordinatorCoachSelect from './CoachingCoordinatorCoachSelect';
import BoxBody from 'components/BoxBody';
import Button from 'components/ButtonDeprecated';
import DatePickerInput from 'components/DatePickerInput';
import ErrorReporter from 'app/components/ErrorReporter';
import EventTracking from 'lib/EventTracking';
import { getCoachAvailabilityForEvent } from 'coaching/actions/CoachingActions';
import Heading from 'components/Heading';
import Icon from 'components/Icon';
import Label from 'components/Label';
import MediaObject from 'components/MediaObject';
import Notice from 'components/Notice';
import Row from 'components/Row';
import Select from 'components/Select';
import Text from 'components/Text';
import TimePickerInput from 'components/TimePickerInput';
import ToastActions from 'actions/ToastActions';
import {
  ParticipantRow,
  CoachRow,
} from 'coaching/components/CoachingCoordinatorView/CoachingCoordinatorPeopleListView';
import { CoachingSessionInput } from 'globalTypes';
import { formatRecurrenceRules } from 'lib/dateTimeFormat';

export type Props = {
  participant: ParticipantRow;
  onCreateCoachingSession: (input: CoachingSessionInput) => Promise<void>;
  onHide: () => void;
  coaches?: CoachRow[];
  loadingCoaches: boolean;
  coachesError?: string;
  coachListHasNextPage?: boolean;
  onNextCoachPage: () => Promise<void>;
};

type State = {
  duration: CoachingSessionDuration;
  frequency: CoachingSessionFrequency;
  coachUuid?: string;

  // when the user inputs these `Date`s into DatePickerInputs,
  // the input value is set to the user's local time, but is
  // converted to UTC when we store it in the component state
  startDateTime: moment.Moment;
  recurrenceUntil?: moment.Moment;
  relativeUtcOffset?: number;

  // these fields come from checking the current event definition
  // against a coach's availability
  loadingCoachAvailabilityConflict: boolean;
  coachIsFullyAvailable?: boolean;
  unavailableDates?: string[];
};

export default class CoachingCoordinatorAddSession extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    // set the default startDateTime to be exactly on the hour
    // since we are only scheduling sessions on the hour
    const startDateTime = moment();
    startDateTime.add(1, 'day');
    startDateTime.hours(16);
    startDateTime.minutes(0);
    startDateTime.seconds(0);
    startDateTime.milliseconds(0);

    this.state = {
      duration: 40,
      startDateTime: moment.utc(startDateTime),
      frequency: 'once',
      loadingCoachAvailabilityConflict: false,
    };
  }

  async componentDidMount() {
    this.trackEvent('view');
  }

  async componentDidUpdate(_prevProps: Props, prevState: State) {
    const fieldsToCheck: (keyof State)[] = [
      'duration',
      'startDateTime',
      'frequency',
      'recurrenceUntil',
      'coachUuid',
    ];

    if (fieldsToCheck.some(field => prevState[field] !== this.state[field])) {
      await this.setCoachAvailabilityInfo();
    }
  }

  trackEvent(event: string) {
    EventTracking.trackKey(`coaching.coordinator_tool.add_session.${event}`);
  }

  cannotScheduleSession = () => {
    const { loadingCoachAvailabilityConflict, coachUuid, unavailableDates } = this.state;
    return (
      loadingCoachAvailabilityConflict ||
      this.sessionIsInThePast() ||
      !coachUuid ||
      (!!unavailableDates && unavailableDates.length > 5)
    );
  };

  sessionIsInThePast = () => {
    return this.state.startDateTime.isBefore(new Date());
  };

  setCoachAvailabilityInfo = throttle(async () => {
    if (!this.state.coachUuid) {
      return;
    }

    const eventDefinition = this.getCurrentEventDefinition(false);
    this.setState({ loadingCoachAvailabilityConflict: true });
    const resp = await getCoachAvailabilityForEvent(
      this.state.coachUuid,
      eventDefinition,
      this.props.participant.uuid
    );
    if (resp) {
      this.trackEvent('coach_unavailabilities.view');
      this.setState({
        coachIsFullyAvailable: resp.coaching?.getCoachAvailabilityForEvent.coachIsFullyAvailable,
        unavailableDates: resp.coaching?.getCoachAvailabilityForEvent.unavailableDates,
      });
    }

    this.setState({ loadingCoachAvailabilityConflict: false });
  }, 200);

  setDuration = (duration: CoachingSessionDuration) => {
    this.trackEvent('duration.change');
    this.setState({ duration });
  };

  setStartDate = (newStartDateTime?: Date) => {
    if (newStartDateTime) {
      this.trackEvent('start_date.change');
      const { startDateTime: existingStartDateTime } = this.state;
      // only modify the date components, not the time components
      // because they are controlled by separate UI elements
      this.setState({
        startDateTime: moment.utc({
          year: newStartDateTime.getUTCFullYear(),
          month: newStartDateTime.getUTCMonth(),
          day: newStartDateTime.getUTCDate(),
          hour: existingStartDateTime.hours(),
          minute: existingStartDateTime.minutes(),
          second: existingStartDateTime.seconds(),
          milliseconds: existingStartDateTime.milliseconds(),
        }),
      });
    } else {
      ToastActions.showDefaultErrorToast();
    }
  };

  setStartTime = (startTime?: Date) => {
    if (startTime) {
      this.trackEvent('start_time.change');
      const newHours = startTime.getUTCHours();
      const newDateTime = moment.utc(startTime);
      newDateTime.hours(newHours);
      this.setState({ startDateTime: newDateTime });
    } else {
      ToastActions.showDefaultErrorToast();
    }
  };

  setRecurrenceUntil = (newRecurrenceUntilDate?: Date) => {
    if (newRecurrenceUntilDate) {
      this.trackEvent('end_date.change');
      // set the endDateTime to the last possible moment on the selected date
      // in order to correctly capture all potential coaching sessions
      this.setState({
        recurrenceUntil: moment.utc({
          year: newRecurrenceUntilDate.getUTCFullYear(),
          month: newRecurrenceUntilDate.getUTCMonth(),
          date: newRecurrenceUntilDate.getUTCDate(),
          hour: 23,
          minute: 59,
          second: 59,
          millisecond: 59,
        }),
      });
    } else {
      ToastActions.showDefaultErrorToast();
    }
  };

  setFrequency = (frequency: CoachingSessionFrequency) => {
    this.trackEvent('frequency.change');
    this.setState({ frequency }, () => {
      if (frequency === 'weeklyWithEnd' || frequency === 'everyOtherWeekWithEnd') {
        this.setRecurrenceUntil(this.state.startDateTime.toDate());
        this.trackEvent('end_date.view');
      } else {
        this.setState({ recurrenceUntil: undefined });
      }
    });
  };

  getSelectedCoach = () => {
    return this.props.coaches?.find(coach => coach.uuid === this.state.coachUuid);
  };

  getRecurrenceRules = (excludeUnavailableDates: boolean) => {
    const { frequency, recurrenceUntil, unavailableDates } = this.state;
    if (frequency === 'once') {
      return;
    }

    const isEveryOtherWeek =
      frequency === 'everyOtherWeekOngoing' || frequency === 'everyOtherWeekWithEnd';

    return formatRecurrenceRules({
      interval: isEveryOtherWeek ? 2 : 1,
      until: recurrenceUntil?.toDate(),
      exceptionDates: unavailableDates,
      excludeUnavailableDates,
    });
  };

  getCurrentEventDefinition = (excludeUnavailableDates: boolean) => {
    const { duration, startDateTime } = this.state;
    const startDateTimeString = startDateTime
      .clone()
      .utc()
      .toISOString();
    // we can calculate the `endDateTime` of the first session instance
    // by setting the minutes component of the startDateTime to`duration`
    // since we always start at the top of the hour.

    const endDateTime = moment.utc(startDateTimeString).minutes(duration);

    return {
      startDateTime: startDateTimeString,
      endDateTime: endDateTime.toISOString(),
      recurrence: this.getRecurrenceRules(excludeUnavailableDates),
      timezone: this.getSelectedCoach()?.timezone ?? this.props.participant.timezone,
    };
  };

  handleScheduleSessions = async () => {
    this.trackEvent('schedule_session.click');
    const { coachUuid } = this.state;
    if (!coachUuid) {
      console.error('CoachingCoordinatorAddSession - tried to schedule a session without a coach');
      return;
    }

    await this.props.onCreateCoachingSession({
      participantUuid: this.props.participant.uuid,
      coachUuid,
      eventDefinition: this.getCurrentEventDefinition(true),
    });
    this.props.onHide();
  };

  renderCoachDropdown = () => {
    return (
      <CoachingCoordinatorCoachSelect
        data-test="coach-dropdown"
        onNextPage={this.props.onNextCoachPage}
        hasNextPage={this.props.coachListHasNextPage}
        coaches={this.props.coaches}
        onSelectCoach={(coachItem: React.ReactElement<AsyncSelectItemProps>) => {
          this.trackEvent('coach.change');
          if (coachItem.props.id) {
            this.setState({ coachUuid: `${coachItem.props.id}` }, () => {
              const coachTimeZone = this.getSelectedCoach()?.timezone;

              if (coachTimeZone) {
                const coordinatorUtcOffset = moment()
                  .tz(moment.tz.guess())
                  .utcOffset();
                const coachUtcOffset = moment()
                  .tz(coachTimeZone)
                  .utcOffset();
                const relativeUtcOffset = coordinatorUtcOffset - coachUtcOffset;

                this.setState({ relativeUtcOffset });
              } else {
                this.setState({ relativeUtcOffset: undefined });
              }
            });
          }
        }}
        validStatuses={['Active', 'No auto schedule']}
      />
    );
  };

  renderDurationCoin = (durationValue: CoachingSessionDuration) => {
    const isSelected = this.state.duration === durationValue;
    const onClick = () => this.setDuration(durationValue);
    return (
      <Coin
        onClick={onClick}
        width={32}
        border={isSelected ? 'mascot' : 'percussion'}
        thick={isSelected}
        color="whiteout"
        size="xlarge"
        data-test={`duration-coin-${durationValue}`}>
        <Text lineHeight="flush">{durationValue}</Text>
      </Coin>
    );
  };

  renderDurationField = () => {
    return (
      <React.Fragment>
        <Label>Duration</Label>
        <MediaObject gutter="xsmall" center>
          <MediaObject gutter="xsmall">
            {this.renderDurationCoin(40)}
            {this.renderDurationCoin(60)}
          </MediaObject>
          <Text>min</Text>
        </MediaObject>
      </React.Fragment>
    );
  };

  renderSchedulingErrors = () => {
    const selectedCoach = this.getSelectedCoach();
    const selectedCoachName = selectedCoach?.name ?? 'The selected coach';
    if (this.sessionIsInThePast()) {
      return (
        <Notice color="paper" noShadow noBorderRadius>
          You cannot schedule sessions in the past.
        </Notice>
      );
    }
    const eventDefinition = this.getCurrentEventDefinition(false);
    return (
      <CoachingCoordinatorAvailabilityConflicts
        coachUuid={selectedCoach?.uuid}
        coachName={selectedCoachName}
        proposedEventTimeframe={eventDefinition}
        unavailableDates={this.state.unavailableDates}
        coachIsFullyAvailable={this.state.coachIsFullyAvailable}
        loading={this.state.loadingCoachAvailabilityConflict}
        eventFrequency={this.state.frequency}
        relativeUtcOffset={this.state.relativeUtcOffset}
      />
    );
  };

  render() {
    const { coachesError } = this.props;
    const { frequency, startDateTime, recurrenceUntil } = this.state;
    const frequencyWithEndDate =
      frequency === 'weeklyWithEnd' || frequency === 'everyOtherWeekWithEnd';

    if (coachesError) {
      return <ErrorReporter error={coachesError ?? 'Could not load coaches'} />;
    }

    return (
      <BoxBody>
        <Row>
          <Text align="right">
            <Button
              level="icon"
              onClick={() => {
                this.trackEvent('cancel.click');
                this.props.onHide();
              }}>
              <Icon name="x" />
            </Button>
          </Text>
        </Row>
        <Row>
          <Heading level="4">Add session</Heading>
        </Row>
        <Row>{this.renderDurationField()}</Row>
        <Row>
          <Columns2>
            <div>
              <Label>Date</Label>
              <DatePickerInput
                full
                format="ddd, MMMM d"
                min={new Date()}
                value={startDateTime.toDate()}
                onChange={this.setStartDate}
                data-test="start-date-dropdown"
              />
            </div>
            <div>
              <Label>Start time</Label>
              <TimePickerInput
                data-test="start-time-dropdown"
                full
                step={60}
                value={startDateTime.toDate()}
                onChange={this.setStartTime}
              />
            </div>
          </Columns2>
        </Row>
        <Row>
          <Columns2>
            <div>
              <Label>Frequency</Label>
              <Select
                full
                options={CoachingConstants.COACHING_FREQUENCIES_FOR_COORDINATOR}
                onChange={this.setFrequency}
                value={frequency}
                data-test="session-frequency-dropdown"
              />
            </div>
            {frequencyWithEndDate && (
              <div>
                <Label>End date</Label>
                <DatePickerInput
                  full
                  format="ddd, MMMM d"
                  min={startDateTime.toDate()}
                  value={recurrenceUntil?.toDate()}
                  onChange={this.setRecurrenceUntil}
                  data-test="recurring-until-dropdown"
                />
              </div>
            )}
          </Columns2>
        </Row>
        <Row>
          <Columns2>
            {this.renderCoachDropdown()}
            <span />
          </Columns2>
        </Row>
        <Row>{this.renderSchedulingErrors()}</Row>
        <Button
          data-test="schedule-sessions-button"
          onClick={this.handleScheduleSessions}
          disabled={this.cannotScheduleSession()}>
          Schedule sessions
        </Button>
      </BoxBody>
    );
  }
}
